/**
 * Utility functions for handling API errors in incident report forms
 */

/**
 * Extract and format user-friendly error messages from API response
 * @param {Object} errors - Error object from API response
 * @returns {string} - User-friendly error message
 */
export const getErrorMessage = (errors) => {
  if (!errors) {
    return "An unexpected error occurred. Please try again.";
  }

  // Handle non-field errors (like rate limiting, general validation errors)
  if (errors.non_field_errors && errors.non_field_errors.length > 0) {
    const errorMessage = errors.non_field_errors[0];

    // Rate limiting error
    if (errorMessage.includes("wait") && errorMessage.includes("minutes")) {
      return errorMessage;
    }

    // reCAPTCHA errors
    if (errorMessage.toLowerCase().includes("recaptcha")) {
      return "Please complete the reCAPTCHA verification.";
    }

    // General validation errors
    return errorMessage;
  }

  // Handle specific field errors
  if (errors.recaptcha) {
    return "Please complete the reCAPTCHA verification.";
  }

  if (errors.malware_samples || errors.malware_sample) {
    return "Malware sample validation failed. Please ensure you upload a ZIP file.";
  }

  if (
    errors.additional_files ||
    errors.supporting_files ||
    errors.evidence_files
  ) {
    return "File validation failed. Please check your uploaded files.";
  }

  if (errors.email) {
    const emailError = Array.isArray(errors.email) ? errors.email[0] : errors.email;
    return emailError;
  }

  // Check for other common field errors
  const commonFields = [
    "full_name",
    "organization",
    "phone_number",
    "incident_description",
  ];
  for (const field of commonFields) {
    if (errors[field]) {
      const fieldError = Array.isArray(errors[field])
        ? errors[field][0]
        : errors[field];
      return fieldError;
    }
  }

  // If we have any errors but none of the above, return the first available error message
  const errorKeys = Object.keys(errors);
  if (errorKeys.length > 0) {
    const firstKey = errorKeys[0];
    const firstError = Array.isArray(errors[firstKey]) ? errors[firstKey][0] : errors[firstKey];
    if (typeof firstError === 'string') return firstError;
    return "Please check the form for errors and try again.";
  }

  return "An unexpected error occurred. Please try again.";
};

/**
 * Handle API errors consistently across all incident report forms
 * @param {Object} error - Axios error object
 * @param {Function} setErrors - Formik setErrors function
 * @param {Function} toast - Toast notification function
 * @param {Function} t - Translation function (optional)
 */
export const handleSubmissionError = (error, setErrors, toast, t = null) => {
  console.error("Submission error:", error);

  if (error.response?.status === 429) {
    toast.error("Rate limit exceeded. Please try again later.");
  } else if (error.response?.data) {
    // Check if response is HTML (nginx error page)
    const contentType = error.response.headers['content-type'] || '';
    const isHtml = contentType.includes('text/html');
    
    if (isHtml) {
      // Handle HTML error responses (nginx errors)
      switch (error.response.status) {
        case 500:
          toast.error('Server error occurred. Please try again later.');
          break;
        case 502:
          toast.error('Service temporarily unavailable. Please try again later.');
          break;
        case 503:
          toast.error('Service unavailable. Please try again later.');
          break;
        case 504:
          toast.error('Request timeout. Please try again.');
          break;
        default:
          toast.error('Server error occurred. Please try again later.');
      }
      return;
    }
    const errorData = error.response.data;
    const { error_type, message, errors } = errorData;

    // Extract error_type properly (it might be an array from DRF)
    let actualErrorType = error_type;
    if (Array.isArray(error_type) && error_type.length > 0) {
      actualErrorType = error_type[0];
    }

    // Debug: Log error details
    console.log(
      "Error response size:",
      JSON.stringify(errorData).length,
      "bytes"
    );
    console.log("Error keys:", Object.keys(errorData));
    console.log("Error type:", actualErrorType);

    // Handle new structured error format
    if (actualErrorType) {
      // Show specific user-friendly messages based on error type
      switch (actualErrorType) {
        case "rate_limit":
          toast.error(typeof errors === "string" ? errors : "Too many submissions. Please wait before trying again.");
          break;
        case "duplicate":
          toast.error(
            typeof errors === "string" ? errors : "This appears to be a duplicate report. Please avoid resubmitting."
          );
          break;
        case "similar":
          toast.error(typeof errors === "string" ? errors : "You've already reported a similar incident today.");
          break;
        case "validation_error":
          let validationMsg = message || "Please check the form for errors and try again.";
          if (errors && typeof errors === "object") {
            const specificMsg = getErrorMessage(errors);
            if (specificMsg !== "An unexpected error occurred. Please try again." && 
                specificMsg !== "Please check the form for errors and try again.") {
              validationMsg = specificMsg;
            }
          }
          toast.error(validationMsg);
          break;
        case "system_error":
          toast.error("A system error occurred. Please try again later.");
          break;
        default:
          toast.error(
            message ||
              (t
                ? t("incident report.ui.submission_error")
                : "An error occurred while submitting the report.")
          );
      }

      // Set field-specific errors if available
      if (errors && setErrors && typeof errors === "object") {
        const formErrors = {};
        Object.keys(errors).forEach((key) => {
          if (key !== "non_field_errors" && key !== "error_type") {
            formErrors[key] = Array.isArray(errors[key])
              ? errors[key][0]
              : errors[key];
          }
        });
        setErrors(formErrors);
      }
    }
    // Handle legacy error format for backward compatibility
    else if (errorData.errors) {
      const errors = errorData.errors;

      console.log("Legacy error format detected");
      setErrors(errors);

      const userMessage = getErrorMessage(errors);
      toast.error(userMessage);
    }
    // Handle other error formats
    else if (typeof errorData === "string") {
      toast.error(errorData);
    } else if (message) {
      toast.error(message);
    } else {
      toast.error("Invalid request. Please check your input and try again.");
    }
  } else if (error.response?.status === 403) {
    const message = t
      ? t("incident report.ui.permission_error")
      : "Permission denied. Please check your submission.";
    toast.error(message);
  } else if (error.response?.status >= 500) {
    toast.error("Server error. Please try again later.");
  } else {
    toast.error("An error occurred while submitting the report.");
  }
};
